/**
 * PostHog analytics wrapper — no-ops when REACT_APP_POSTHOG_KEY is unset.
 *
 * Tracks specific events only — no autocapture of raw clicks.
 * Pageviews are tracked manually via `AnalyticsPageTracker` so we don't
 * leak query params or task IDs into PostHog as route segments.
 */
import logger from "@/lib/logger";

let posthog = null;
let _ready = false;

export function initAnalytics() {
  const key = process.env.REACT_APP_POSTHOG_KEY;
  if (!key) return;
  const api_host = process.env.REACT_APP_POSTHOG_HOST || "https://us.posthog.com";
  import("posthog-js").then((mod) => {
    posthog = mod.default;
    posthog.init(key, {
      api_host,
      autocapture: false,
      capture_pageview: false,          // we call capture('$pageview') manually
      capture_pageleave: true,
      persistence: "localStorage",
      disable_session_recording: true,  // opt-in only
    });
    _ready = true;
  }).catch((e) => logger.warn("analytics: posthog-js failed to load", e));
}

// Fire-and-forget product event. Never throws into the UI; no-ops until PostHog is ready.
export function track(event, props = {}) {
  if (!posthog || !_ready) return;
  try {
    posthog.capture(event, props);
  } catch (e) {
    logger.warn(`analytics: capture failed for ${event}`, e);
  }
}

export const trackEvent = track;

// Milliseconds a task was open, from its ISO created_at; null when unknown.
export function timeOpenMs(createdAt) {
  const t = createdAt ? Date.parse(createdAt) : NaN;
  return Number.isNaN(t) ? null : Math.max(0, Date.now() - t);
}

export function identifyUser(userId, traits = {}) {
  if (posthog && _ready) posthog.identify(userId, traits);
}

export function resetAnalytics() {
  if (posthog && _ready) posthog.reset();
}

export function trackPageview(pathname) {
  track("$pageview", { $current_url: window.location.origin + pathname });
}
